import clsx from "clsx";
import { parseISO } from "date-fns";
import { Tab, TabList } from "react-aria-components";
import { dateName } from "@/Utils/DateUtils";
import styles from "./DailyOverviewsPanel.module.scss";
import { Text } from "@medoma/components/Text/Text";
import { t } from "@lingui/core/macro";
import type { DailyOverviewData } from "@/api/Capacity";
import {
  calculateVisitsPerShift,
  dailyOverviewsCompetences,
} from "./dailyOverviewsUtils";
import { medicalCompetenceDictionary } from "@medoma/models/shifts";
import { VisitsPerShiftChip } from "../VisitsPerShiftChip";

const PopoverContent = () => {
  const trackedCompetencesLabel = dailyOverviewsCompetences
    .map((competence) => t(medicalCompetenceDictionary[competence].long))
    .join(", ");

  return (
    <>
      <Text element="p">{t`Genomsnittligt antal besök per pass.`}</Text>
      <Text element="p">
        {t`Gäller följande kompetenser`}: {trackedCompetencesLabel}
      </Text>
    </>
  );
};

const DailyOverviewTab = ({ tab }: { tab: DailyOverviewData }) => {
  const trackedCompetenceCapacity = tab.competenceCapacity.filter(
    (competence) => dailyOverviewsCompetences.includes(competence.competence),
  );
  const visitsPerShift = calculateVisitsPerShift(trackedCompetenceCapacity);
  const formattedTabDate = dateName(parseISO(tab.date), {
    monthFormat: "numeric",
  });

  return (
    <Tab
      id={tab.date}
      className={({ isSelected }) =>
        clsx(styles.tab, isSelected ? styles.selectedTab : styles.unSelectedTab)
      }
    >
      {({ isSelected }) => (
        <>
          <Text element="span" weight="medium" className={styles.tabDate}>
            {formattedTabDate}
          </Text>
          <div className={styles.metrics}>
            <div className={styles.admittedPatients}>
              <Text className={styles.count} element="span">
                {tab.admittedPatientsCount}
              </Text>
              <Text element="span" size="small" color="faded">
                {t`inskrivna`}
              </Text>
            </div>
            <div className={styles.visitsPerShiftRow}>
              <VisitsPerShiftChip
                visitsPerShift={visitsPerShift}
                shape="pill"
                popover={{ enabled: isSelected, content: <PopoverContent /> }}
              />
            </div>
          </div>
        </>
      )}
    </Tab>
  );
};

export const DailyOverviewsPanel = ({
  dailyOverviewsData,
}: {
  dailyOverviewsData: DailyOverviewData[];
}) => (
  <TabList aria-label={t`Översikt i dagar`} className={styles.panel}>
    {dailyOverviewsData.map((tab) => (
      <DailyOverviewTab key={tab.date} tab={tab} />
    ))}
  </TabList>
);
